/**
 * Ticks every [data-sale-timer] countdown on the page down to local midnight,
 * matching the product page's sale bar.
 *
 * One interval queries the DOM fresh each second, so bars that arrive later —
 * the cart summary is swapped in by the Section Rendering API on every cart
 * change — pick up the right time on the next tick with no extra wiring.
 *
 * The digits roll like an odometer using NumberFlow (assets/number-flow.js),
 * imported lazily from the URL in the bar's data-number-flow-src. Until it has
 * loaded — or if it fails to, or the shopper prefers reduced motion, in which
 * case it isn't fetched at all — the values are plain text, as before.
 */
// Loaded as a classic script by both the cart drawer and the header's sale
// countdown block, so it can run twice on a page: start only one timer, and keep
// the declarations in this block so a second run doesn't redeclare them (a
// top-level `const` would throw a SyntaxError before the guard is reached).
if (!window.saleTimerStarted) {
  window.saleTimerStarted = true;

  const pad = (n) => String(n).padStart(2, '0');
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  // null: not requested yet; false: unavailable (plain text); true: ready.
  let rolling = null;

  const loadRolling = (bar) => {
    rolling = false;
    const src = bar.dataset.numberFlowSrc;
    if (reduceMotion || !src) return;
    // asset_url is protocol-relative (//…/assets/…); make it absolute for import().
    import(new URL(src, document.baseURI).href)
      .then(() => customElements.whenDefined('number-flow'))
      .then(() => {
        rolling = true;
        tick();
      })
      .catch(() => {});
  };

  // Hours' tens digit never exceeds 2, minutes' and seconds' never exceed 5,
  // so 00 → 59 rolls back one step on each digit instead of spinning 0 → 5.
  const digits = { hours: { 1: { max: 2 } }, minutes: { 1: { max: 5 } }, seconds: { 1: { max: 5 } } };

  const render = (el, unit, value) => {
    if (!rolling) {
      el.textContent = pad(value);
      return;
    }
    let flow = el.firstElementChild;
    if (flow?.localName !== 'number-flow') {
      // A bar we haven't upgraded yet (first load, or freshly re-rendered):
      // its first update() sets the value without animating.
      flow = document.createElement('number-flow');
      flow.format = { minimumIntegerDigits: 2, useGrouping: false };
      flow.digits = digits[unit];
      flow.trend = -1; // a countdown: always roll the way a decrement does
      el.replaceChildren(flow);
    }
    if (flow.value !== value) flow.update(value);
  };

  const tick = () => {
    const now = new Date();
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);

    const remaining = Math.max(0, end - now);
    const hours = Math.floor(remaining / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    for (const bar of document.querySelectorAll('[data-sale-timer]')) {
      if (rolling === null) loadRolling(bar);
      render(bar.querySelector('[data-sale-timer-hours]'), 'hours', hours);
      render(bar.querySelector('[data-sale-timer-minutes]'), 'minutes', minutes);
      render(bar.querySelector('[data-sale-timer-seconds]'), 'seconds', seconds);
    }
  };

  tick();
  setInterval(tick, 1000);
}
